import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "@/types/store";
import styles from "./FlaggedNetworkRequests.module.css";
import { getAPIToken } from "@/utils/request";
import * as accountsReducer from "@/containers/Accounts/reducer";
import * as openClawReducer from "containers/OpenClaw";
import * as chatGPTReducer from "containers/ChatGPT/reducer";
// ── Icons ──────────────────────────────────────────────────────────────
const RefreshIcon = () => (React.createElement("svg", { viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg" },
    React.createElement("path", { d: "M21 2v6h-6M3 22v-6h6", strokeLinecap: "round", strokeLinejoin: "round" }),
    React.createElement("path", { d: "M3 12a9 9 0 0 1 15.36-6.36L21 8M21 12a9 9 0 0 1-15.36 6.36L3 16", strokeLinecap: "round", strokeLinejoin: "round" })));
// ── Helpers ─────────────────────────────────────────────────────────────
function confidenceClass(pct) {
    if (pct >= 80)
        return styles.confidenceHigh;
    if (pct >= 50)
        return styles.confidenceMed;
    return styles.confidenceLow;
}
// ── Component ──────────────────────────────────────────────────────────
const FlaggedNetworkRequests = ({ sandboxName, baseUrl = undefined, pollInterval = 10000, debug = false }) => {
    const dispatch = useDispatch();
    const [requests, setRequests] = useState([]);
    // Local override map: request id -> the decision we've made in this session.
    // This lets us keep resolved items visible & toggleable even after the server
    // moves them out of "pending".
    const [localStatus, setLocalStatus] = useState({});
    const [filter, setFilter] = useState("pending");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actioning, setActioning] = useState(new Set());
    const [toast, setToast] = useState(null);
    const [spinning, setSpinning] = useState(false);
    const toastTimer = useRef();
    const showToast = useCallback((t) => {
        setToast(t);
        if (toastTimer.current)
            clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(null), 4000);
    }, []);
    const openshellBaseUrl = useSelector((state) => accountsReducer.getOpenShellBaseUrl(state));
    const retBaseUrl = baseUrl ?? openshellBaseUrl;
    const selectedSandbox = useSelector((state) => chatGPTReducer.getSelectedSandbox(state));
    // Resolve the effective status for a request: local override wins over server status.
    const effectiveStatus = useCallback((req) => {
        const override = localStatus[req.id];
        if (override)
            return override;
        if (req.status === "approved" || req.status === "denied")
            return req.status;
        return "pending";
    }, [localStatus]);
    // ── Fetch flagged requests ──────────────────────────────────────────
    const fetchRequests = useCallback(async (silent = false) => {
        if (!silent)
            setLoading(true);
        const apiToken = getAPIToken();
        if (apiToken === undefined) {
            return;
        }
        try {
            const res = await fetch(`${retBaseUrl}/sandboxes/${encodeURIComponent(sandboxName)}/flagged-network-requests`, {
                headers: { Authorization: "Bearer " + apiToken }
            });
            if (!res.ok)
                throw new Error(`${res.status} ${res.statusText}`);
            const data = await res.json();
            // Merge: keep any locally-known requests (e.g. ones we've acted on that
            // the server no longer returns in pending) plus whatever the server sends.
            setRequests((prev) => {
                const byId = new Map();
                for (const r of prev)
                    byId.set(r.id, r);
                for (const r of data)
                    byId.set(r.id, r);
                return Array.from(byId.values());
            });
            setError(null);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Failed to fetch requests");
        }
        finally {
            setLoading(false);
        }
    }, [sandboxName, retBaseUrl]);
    // ── Polling ─────────────────────────────────────────────────────────
    useEffect(() => {
        fetchRequests();
        if (pollInterval > 0) {
            const id = setInterval(() => fetchRequests(true), pollInterval);
            return () => clearInterval(id);
        }
    }, [fetchRequests, pollInterval]);
    // ── Manual refresh with spin animation ──────────────────────────────
    const handleRefresh = useCallback(() => {
        setSpinning(true);
        fetchRequests().finally(() => {
            setTimeout(() => setSpinning(false), 600);
        });
    }, [fetchRequests]);
    // ── Approve / Deny ──────────────────────────────────────────────────
    const handleAction = useCallback(async (request, action) => {
        const requestId = request.id;
        const newStatus = action === "approve" ? "approved" : "denied";
        // If they're clicking the same decision they already made, treat it as a no-op.
        if (effectiveStatus(request) === newStatus)
            return;
        setActioning((prev) => new Set(prev).add(requestId));
        const apiToken = getAPIToken();
        if (apiToken === undefined) {
            setActioning((prev) => {
                const next = new Set(prev);
                next.delete(requestId);
                return next;
            });
            return;
        }
        try {
            const res = await fetch(`${retBaseUrl}/sandboxes/${encodeURIComponent(sandboxName)}/flagged-network-requests/${requestId}/${action}`, { method: "POST", headers: { Authorization: "Bearer " + apiToken } });
            if (!res.ok) {
                const text = await res.text().catch(() => "");
                throw new Error(text || `${res.status} ${res.statusText}`);
            }
            const data = await res.json();
            if (action === "approve") {
                dispatch(openClawReducer.handleOpenClaw("I just approved this policy: " + (request?.rationale ?? request?.rule_name), false, selectedSandbox));
            }
            // Mark resolved locally so the card stays visible and we can change our mind later.
            setLocalStatus((prev) => ({ ...prev, [requestId]: newStatus }));
            showToast({
                type: "success",
                message: `${action === "approve" ? "Approved" : "Denied"} — v${data.policy_version ?? "?"}`
            });
        }
        catch (err) {
            showToast({
                type: "error",
                message: err instanceof Error ? err.message : `Failed to ${action}`
            });
        }
        finally {
            setActioning((prev) => {
                const next = new Set(prev);
                next.delete(requestId);
                return next;
            });
        }
    }, [sandboxName, retBaseUrl, dispatch, selectedSandbox, showToast, effectiveStatus]);
    // ── Derived lists for filtering / counts ────────────────────────────
    const { pending, resolved, visible } = useMemo(() => {
        const p = [];
        const r = [];
        for (const req of requests) {
            if (effectiveStatus(req) === "pending")
                p.push(req);
            else
                r.push(req);
        }
        const v = filter === "pending" ? p : filter === "resolved" ? r : requests;
        return { pending: p, resolved: r, visible: v };
    }, [requests, effectiveStatus, filter]);
    // ── Debug: inject mock data ─────────────────────────────────────────
    const loadTestData = useCallback(() => {
        const mockRequests = [
            {
                id: "mock-1",
                status: "pending",
                rule_name: "allow_registry_npmjs_org_443",
                binary: "/usr/bin/node",
                rationale: "Allow node to connect to registry.npmjs.org:443 (HTTPS).",
                confidence_pct: 65,
                security_notes: "",
                endpoints: "registry.npmjs.org:443",
                hits: ""
            },
            {
                id: "mock-2",
                status: "pending",
                rule_name: "allow_api_github_com_443",
                binary: "/usr/bin/git",
                rationale: "Allow git to connect to api.github.com:443 for repo operations.",
                confidence_pct: 90,
                security_notes: "Trusted endpoint",
                endpoints: "api.github.com:443",
                hits: ""
            },
            {
                id: "mock-3",
                status: "pending",
                rule_name: "allow_pypi_org_443",
                binary: "/usr/bin/python3",
                rationale: "Allow pip to install packages from pypi.org:443.",
                confidence_pct: 30,
                security_notes: "Unverified package source",
                endpoints: "pypi.org:443",
                hits: ""
            }
        ];
        setRequests(mockRequests);
        setLocalStatus({});
        setLoading(false);
        setError(null);
    }, []);
    return (React.createElement("div", { className: styles.container },
        React.createElement("div", { className: "pt-1.5 px-3 flex justify-between" },
            React.createElement("div", { className: styles.tabs },
                React.createElement("button", { className: `${styles.tab}${filter === "pending" ? ` ${styles.tabActive}` : ""}`, onClick: () => setFilter("pending") },
                    "Pending (",
                    pending.length,
                    ")"),
                React.createElement("button", { className: `${styles.tab}${filter === "resolved" ? ` ${styles.tabActive}` : ""}`, onClick: () => setFilter("resolved") },
                    "Resolved (",
                    resolved.length,
                    ")")),
            React.createElement("div", { className: styles.headerActions },
                pending.length > 0 && React.createElement("span", { className: styles.badge }, pending.length),
                debug && (React.createElement("button", { className: styles.refreshBtn, onClick: loadTestData, title: "Load test data", style: { fontSize: 10, width: "auto", padding: "0 6px" } }, "Test")),
                React.createElement("button", { className: `${styles.refreshBtn}${spinning ? ` ${styles.spinning}` : ""}`, onClick: handleRefresh, title: "Refresh" },
                    React.createElement(RefreshIcon, null)))),
        toast && (React.createElement("div", { className: toast.type === "success" ? styles.toastSuccess : styles.toastError }, toast.message)),
        React.createElement("div", { className: styles.list },
            loading && requests.length === 0 && React.createElement("div", { className: styles.loadingState }, "Loading requests\u2026"),
            error && !loading && React.createElement("div", { className: styles.errorState }, error),
            !loading && !error && visible.length === 0 && (React.createElement("div", { className: styles.emptyState }, filter === "pending"
                ? "No pending requests"
                : filter === "resolved"
                    ? "Nothing resolved yet"
                    : "No requests")),
            visible.map((req) => {
                const busy = actioning.has(req.id);
                const status = effectiveStatus(req);
                const isApproved = status === "approved";
                const isDenied = status === "denied";
                const cardClass = [styles.card, isApproved ? styles.cardApproved : "", isDenied ? styles.cardDenied : ""]
                    .filter(Boolean)
                    .join(" ");
                return (React.createElement("div", { key: req.id, className: cardClass },
                    React.createElement("div", { className: styles.cardHeader },
                        React.createElement("span", { className: styles.ruleName }, req.rule_name),
                        React.createElement("span", { className: confidenceClass(req.confidence_pct) },
                            req.confidence_pct,
                            "%")),
                    (isApproved || isDenied) && (React.createElement("div", { className: isApproved ? styles.statusBadgeApproved : styles.statusBadgeDenied }, isApproved ? "✓ Approved" : "✕ Denied")),
                    React.createElement("div", { className: styles.endpoint }, req.endpoints),
                    React.createElement("div", { className: styles.rationale }, req.rationale),
                    React.createElement("div", { className: styles.meta },
                        React.createElement("span", { className: styles.metaLabel }, "binary: "),
                        req.binary,
                        req.security_notes && (React.createElement(React.Fragment, null,
                            " · ",
                            React.createElement("span", { className: styles.metaLabel }, "notes: "),
                            req.security_notes))),
                    React.createElement("div", { className: styles.actions },
                        React.createElement("button", { className: `${styles.approveBtn}${isApproved ? ` ${styles.btnActive}` : ""}`, disabled: busy || isApproved, onClick: () => handleAction(req, "approve"), title: isApproved ? "Already approved" : "Approve" }, busy ? "…" : isApproved ? "✓ Approved" : "✓ Approve"),
                        React.createElement("button", { className: `${styles.denyBtn}${isDenied ? ` ${styles.btnActive}` : ""}`, disabled: busy || isDenied, onClick: () => handleAction(req, "deny"), title: isDenied ? "Already denied" : "Deny" }, busy ? "…" : isDenied ? "✕ Denied" : "✕ Deny"))));
            }))));
};
export default FlaggedNetworkRequests;
